import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Search, Bed, Bath, Maximize, MapPin } from 'lucide-react';
import { getProperties } from '../services/api';
import SEO from '../components/SEO';

const Listings = () => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('price-asc');
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // Helper function to construct image URLs
  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return '';
    return imageUrl.startsWith('http') ? imageUrl : `${BACKEND_URL}${imageUrl}`;
  };

  // Helper function to strip HTML tags and get plain text
  const stripHtml = (html) => {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  useEffect(() => {
    const loadProperties = async () => {
      try {
        const props = await getProperties();
        setProperties(props);
      } catch (error) {
        console.error('Failed to load properties:', error);
      } finally {
        setLoading(false);
      }
    };
    loadProperties();
  }, []);

  // Filter and sort properties
  const filteredProperties = properties
    .filter(property => {
      const matchesSearch = property.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           property.address.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = filterStatus === 'all' || property.status === filterStatus;
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      switch(sortBy) {
        case 'price-asc':
          return a.price - b.price;
        case 'price-desc':
          return b.price - a.price;
        case 'bedrooms':
          return b.bedrooms - a.bedrooms;
        default:
          return 0;
      }
    });

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <SEO 
        title="Property Listings"
        description="Browse quality rental homes and apartments in Lynchburg, VA. Find 2-5 bedroom houses with modern amenities, pet-friendly options, and transparent pricing."
        keywords="Lynchburg VA rentals, available properties Lynchburg, houses for rent near me, apartments Lynchburg Virginia, pet-friendly rentals Lynchburg"
      />
      {/* Header */}
      <section className="bg-otter-green dark:bg-otter-green-700 py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Property Listings</h1>
          <p className="text-xl text-blue-50 max-w-2xl">
            Browse our selection of quality rental properties in Lynchburg, VA
          </p>
        </div>
      </section>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Search and Filters */}
        <Card className="mb-8 dark:bg-gray-900 dark:border-gray-800">
          <CardContent className="p-6">
            <div className="grid md:grid-cols-3 gap-4">
              <div className="md:col-span-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Search by title or address..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <div>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger>
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Properties</SelectItem>
                    <SelectItem value="available">Available</SelectItem>
                    <SelectItem value="rented">Rented</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="price-asc">Price: Low to High</SelectItem>
                    <SelectItem value="price-desc">Price: High to Low</SelectItem>
                    <SelectItem value="bedrooms">Bedrooms: Most</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Results Count */}
        <div className="mb-6">
          <p className="text-gray-600 dark:text-gray-400">
            Showing <span className="font-semibold text-gray-900 dark:text-white">{filteredProperties.length}</span> {filteredProperties.length === 1 ? 'property' : 'properties'}
          </p>
        </div>

        {/* Property Grid */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-gray-600 dark:text-gray-400">Loading properties...</p>
          </div>
        ) : filteredProperties.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProperties.map((property) => (
              <Card key={property.id} className="overflow-hidden hover:shadow-xl transition-all duration-300 group dark:bg-gray-900 dark:border-gray-800">
                {property.status === 'available' ? (
                  <Link to={`/property/${property.slug}`}>
                    <div className="relative h-64 overflow-hidden">
                      <img
                        src={getImageUrl(property.image)}
                        alt={property.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                      <div className="absolute top-4 right-4">
                        <Badge className="bg-otter-green text-white">Available</Badge>
                      </div>
                    </div>
                  </Link>
                ) : property.status === 'coming_soon' ? (
                  <Link to={`/property/${property.slug}`}>
                    <div className="relative h-64 overflow-hidden">
                      <img
                        src={getImageUrl(property.image)}
                        alt={property.title}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                      />
                      <div className="absolute top-4 right-4">
                        <Badge className="bg-otter-teal hover:bg-otter-teal text-white">Coming Soon</Badge>
                      </div>
                    </div>
                  </Link>
                ) : (
                  <div className="relative h-64 overflow-hidden opacity-75 cursor-not-allowed">
                    <img
                      src={getImageUrl(property.image)}
                      alt={property.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-4 right-4">
                      <Badge className="bg-gray-600 text-white">Rented</Badge>
                    </div>
                  </div>
                )}
                <CardContent className="p-6">
                  {property.status === 'available' || property.status === 'coming_soon' ? (
                    <Link to={`/property/${property.slug}`}>
                      <h3 className="text-xl font-semibold mb-2 group-hover:text-otter-green dark:group-hover:text-otter-green-300 transition-colors dark:text-white">
                        {property.title}
                      </h3>
                    </Link>
                  ) : (
                    <h3 className="text-xl font-semibold mb-2 dark:text-white opacity-75">
                      {property.title}
                    </h3>
                  )}
                  <div className="flex items-start gap-2 text-gray-600 dark:text-gray-400 text-sm mb-4">
                    <MapPin className="h-4 w-4 mt-1 flex-shrink-0" />
                    <span>{property.address}</span>
                  </div>
                  <div className="flex items-center justify-between mb-4 pb-4 border-b dark:border-gray-700">
                    <span className="text-3xl font-bold text-otter-green dark:text-otter-green-400">${property.price}<span className="text-base font-normal text-gray-600 dark:text-gray-400">/mo</span></span>
                  </div>
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <div className="flex items-center gap-2 text-sm dark:text-gray-300">
                      <Bed className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                      <span className="font-medium">{property.bedrooms}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm dark:text-gray-300">
                      <Bath className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                      <span className="font-medium">{property.bathrooms}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm dark:text-gray-300">
                      <Maximize className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                      <span className="font-medium">{property.sqft}</span>
                    </div>
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 text-sm mb-4 line-clamp-2">{stripHtml(property.description)}</p>
                  {property.status === 'available' ? (
                    <div className="flex gap-2">
                      <Link to={`/property/${property.slug}`} className="flex-1">
                        <Button className="w-full bg-otter-green hover:bg-otter-green-600 text-white">
                          View Details
                        </Button>
                      </Link>
                      {property.applicationLink && (
                        <a 
                          href={property.applicationLink} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex-1"
                        >
                          <Button className="w-full bg-otter-teal hover:bg-otter-teal/90 text-white border-2 border-otter-teal">
                            Apply Now
                          </Button>
                        </a>
                      )}
                    </div>
                  ) : property.status === 'coming_soon' ? (
                    <Link to={`/property/${property.slug}`} className="w-full">
                      <Button className="w-full bg-otter-green hover:bg-otter-green-600 text-white">
                        View Details
                      </Button>
                    </Link>
                  ) : (
                    <Button className="w-full" disabled>
                      Not Available
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="p-12 text-center dark:bg-gray-900 dark:border-gray-800">
            <p className="text-gray-600 dark:text-gray-400 text-lg">No properties found matching your criteria.</p>
            <Button 
              onClick={() => {
                setSearchTerm('');
                setFilterStatus('all');
              }}
              variant="outline"
              className="mt-4"
            >
              Clear Filters
            </Button>
          </Card>
        )}
      </div>

      {/* CTA Section */}
      <section className="py-16 bg-white dark:bg-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">Can't Find What You're Looking For?</h2>
          <p className="text-lg text-gray-600 dark:text-gray-400 mb-6 max-w-2xl mx-auto">
            Contact us to discuss your specific needs. We're always adding new properties to our portfolio.
          </p>
          <Button size="lg" className="bg-otter-green hover:bg-otter-green-600 text-white">
            Contact Us Today
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Listings;